'use client';

import { useState, useEffect } from 'react';
import ImageUpload from './ImageUpload';
import ColorPicker from './ColorPicker';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';

const LoginCustomizationPanel = ({ selectedClient, clients, slugs }) => {
  const [customizations, setCustomizations] = useState([]);
  const [selectedCustomization, setSelectedCustomization] = useState(null);
  const [formData, setFormData] = useState({
    header_text: 'Welcome Back',
    button_color: '#007bff',
    logo_url: '',
    background_image_url: '',
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [uploadingStates, setUploadingStates] = useState(false);
  const [loginLogoUrl, setLoginLogoUrl] = useState();

  useEffect(() => {
    if (selectedClient || slugs) {
      setCustomizations([]);
      setSelectedCustomization(null);
      fetchCustomizations();
    }
  }, [selectedClient, slugs]);

  const fetchCustomizations = async () => {
    try {
      const response = await fetch(`/api/login-customizations/${selectedClient}`);
      const data = await response.json();
      setCustomizations(data.data);

      // Select first customization if available
      if (data.length > 0) {
        // setSelectedCustomization(data[0])
        setFormData({
          header_text: data[0].header_text,
          button_color: data[0].button_color,
          logo_url: data[0].logo_url || '',
          background_image_url: data[0].background_image_url || '',
        });
      }
    } catch (error) {
      console.error('Error fetching customizations:', error);
      setMessage('Error fetching login customizations');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const saveCustomization = async () => {
    if (!selectedCustomization) {
      setMessage('Please select a login customization first');
      return;
    }

    console.log('selectedCustomization.id', selectedCustomization);

    setLoading(true);
    try {
      const response = await fetch(`/api/login-customizations/${selectedCustomization._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: selectedCustomization._id,
          ...formData,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Login customization saved successfully!');
        fetchCustomizations();
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage(data.error || 'Error saving customization');
      }
    } catch (error) {
      console.error('Error saving customization:', error);
      setMessage('Error saving login customization');
    }
    setLoading(false);
  };

  const selectCustomization = customization => {
    setSelectedCustomization(customization);
    setFormData({
      header_text: customization.header_text,
      button_color: customization.button_color,
      logo_url: customization.logo_url || '',
      background_image_url: customization.background_image_url || '',
    });
  };

  const createDropHandler = setMedia => acceptedFiles => {
    if (acceptedFiles.length > 0) {
      setMedia(acceptedFiles[0]);
    }
  };

  // Common upload handler
  const handleUpload = async file => {
    if (!file) {
      alert(`Please select a file!`);
      return;
    }

    // Set the specific position to uploading
    setUploadingStates(true);

    try {
      // Step 1: Get Pre-signed URL from the Backend
      const response = await axios.get('/generate-signed-url', {
        params: {
          fileName: file.name,
          fileType: file.type,
        },
      });

      const { url } = response.data;

      // Step 2: Upload the File to S3
      await axios.put(url, file, {
        headers: {
          'Content-Type': file.type,
        },
      });

      // Step 3: Save Uploaded File Information
      const fileUrl = url.split('?')[0];

      console.log('fileUrl', fileUrl);

      setFormData(prevSettings => ({
        ...prevSettings,
        logo_url: fileUrl,
      }));

      setUploadingStates(false);
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  if (!selectedClient) {
    return (
      <div className="card">
        <div className="card-header">
          <h5 className="card-title mb-0">
            <i className="fas fa-paint-brush me-2"></i>
            Login Page Customization
          </h5>
        </div>
        <div className="card-body">
          <div className="text-center py-4">
            <i className="fas fa-user-plus mb-3" style={{ fontSize: '3rem', opacity: 0.5 }}></i>
            <p className="text-muted mb-0">Please select a client first</p>
            <small className="text-muted">Choose a client to customize their login page</small>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      {/* <div className="card-header">
        <h5 className="card-title mb-0">
          <i className="fas fa-paint-brush me-2"></i>
          Login Page Customization
        </h5>
        <p className="card-text mb-0 mt-2 opacity-75">Customize the appearance of client login pages</p>
      </div> */}
      <div className="card-body">
        {message && (
          <div
            className={`alert ${message.includes('Error') ? 'alert-danger' : 'alert-success'} alert-dismissible fade show`}
          >
            <i
              className={`fas ${message.includes('Error') ? 'fa-exclamation-triangle' : 'fa-check-circle'} me-2`}
            ></i>
            {message}
            <button type="button" className="btn-close" onClick={() => setMessage('')}></button>
          </div>
        )}

        {customizations.length > 0 && (
          <div className="row">
            {/* Customization Selection */}
            <div className="col-md-4 mb-4">
              <h6 className="fw-bold mb-3">
                <i className="fas fa-list me-2"></i>
                Select Login Page
              </h6>
              <div className="d-grid gap-2">
                {customizations.map(customization => (
                  <div
                    key={customization._id}
                    className={`card cursor-pointer border-2 ${
                      selectedCustomization?._id === customization._id ? 'border' : ''
                    }`}
                    onClick={() => selectCustomization(customization)}
                    style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                    onMouseEnter={e => {
                      if (selectedCustomization?._id !== customization._id) {
                        e.target.classList.add('border-secondary');
                      }
                    }}
                    onMouseLeave={e => {
                      if (selectedCustomization?._id !== customization._id) {
                        e.target.classList.remove('border-secondary');
                      }
                    }}
                  >
                    <div className="card-body p-3">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h6
                          className={`mb-0 fw-bold ${
                            selectedCustomization?._id === customization._id
                              ? 'text-primary'
                              : 'text-dark'
                          }`}
                        >
                          {customization.slug}
                        </h6>
                        <i
                          className={`fas fa-external-link-alt small ${
                            selectedCustomization?._id === customization._id
                              ? 'text-primary'
                              : 'text-muted'
                          }`}
                        ></i>
                      </div>
                      <p className="mb-2 small text-muted">{customization.header_text}</p>
                      <div className="d-flex align-items-center">
                        <div
                          className="rounded-circle me-2"
                          style={{
                            width: '16px',
                            height: '16px',
                            backgroundColor: customization.button_color,
                            border: '2px solid #fff',
                            boxShadow: '0 0 0 1px #dee2e6',
                          }}
                        ></div>
                        <small className="text-muted">{customization.button_color}</small>
                      </div>
                      {/* {selectedCustomization?._id === customization._id && (
                        <div className="mt-2">
                          <span className="badge bg-primary">
                            <i className="fas fa-check me-1"></i>
                            Selected
                          </span>
                        </div>
                      )} */}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Customization Form */}
            <div className="col-md-8">
              {selectedCustomization && (
                <div>
                  <h6 className="fw-bold mb-3">
                    <i className="fas fa-edit me-2"></i>
                    Customize: {selectedCustomization.slug}
                  </h6>

                  {/* Header Text */}
                  <div className="mb-4">
                    <label className="form-label fw-bold">
                      <i className="fas fa-heading me-2"></i>
                      Header Text
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.header_text}
                      onChange={e => handleInputChange('header_text', e.target.value)}
                      placeholder="Enter header text"
                    />
                    <small className="form-text text-muted">
                      This text will appear at the top of the login page
                    </small>
                  </div>

                  {/* Button Color */}
                  <ColorPicker
                    value={formData?.button_color}
                    onChange={color => handleInputChange('button_color', color)}
                    label="Login Button Color"
                  />

                  {/* Logo Upload */}
                  {/* <ImageUpload
                    currentImage={formData.logo_url}
                    onImageUpload={(url) => handleInputChange("logo_url", url)}
                    label="Company Logo"
                  /> */}

                  <div className="row ">
                    {/* Left Top Media */}
                    <div className="col-md-12 ">
                      <div className={`form-group menu-card2 `}>
                        <label>Upload Logo Media:</label>

                        <MediaDropzone
                          onDrop={createDropHandler(setLoginLogoUrl)}
                          selectedFile={loginLogoUrl}
                        />

                        <UploadButton
                          uploading={uploadingStates}
                          onUpload={() => handleUpload(loginLogoUrl)}
                          buttonColor={'#2d3748'}
                        />
                        <UploadedUrl url={formData?.logo_url} />
                      </div>
                    </div>
                  </div>

                  {/* Background Image Upload */}
                  {/* <ImageUpload
                    currentImage={formData.background_image_url}
                    onImageUpload={(url) => handleInputChange("background_image_url", url)}
                    label="Background Image (Optional)"
                  /> */}

                  {/* Save Button */}
                  <div className="d-flex justify-content-end">
                    <button
                      className="btn btn-primary px-4"
                      onClick={saveCustomization}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i>
                          Save Customization
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginCustomizationPanel;

const MediaDropzone = ({ onDrop, selectedFile }) => {
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif'],
      'video/*': ['.mp4', '.mov', '.mp3'],
      'application/pdf': ['.pdf'],
    },
    multiple: false,
  });

  return (
    <div
      {...getRootProps()}
      style={{
        border: '2px dashed #cccccc',
        padding: '20px',
        borderRadius: '5px',
        textAlign: 'center',
        cursor: 'pointer',
      }}
    >
      <input {...getInputProps()} />
      {selectedFile ? (
        <p>{selectedFile.name}</p>
      ) : (
        <p>Drag and drop a file here, or click to select a file</p>
      )}
    </div>
  );
};

// Helper function to check if the file is an image
const getFileType = fileUrl => {
  if (!fileUrl) return 'blank'; // No URL provided

  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.svg'];
  const videoExtensions = ['.mp4', '.mov', '.avi', '.mkv', '.webm'];

  if (imageExtensions.some(ext => fileUrl.toLowerCase().endsWith(ext))) {
    return 'image';
  }
  if (videoExtensions.some(ext => fileUrl.toLowerCase().endsWith(ext))) {
    return 'video';
  }
  return 'other'; // Other file types or unsupported extensions
};

// UploadButton Component
const UploadButton = ({ uploading, onUpload, buttonColor }) => (
  <button
    onClick={onUpload}
    type="button"
    style={{
      marginTop: '20px',
      //   padding: "10px 20px",
      backgroundColor: buttonColor,
      // color: "#FFFFFF",
      border: 'none',
      borderRadius: '5px',
      cursor: 'pointer',
    }}
    disabled={uploading}
  >
    {uploading ? 'Uploading...' : 'Upload'}
  </button>
);

// UploadedUrl Component
const UploadedUrl = ({ url }) =>
  url ? (
    <div
      style={{
        marginTop: '20px',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start', // Adjust alignment of child elements (optional)
        gap: '10px', // Add spacing between child elements (optional)
      }}
    >
      {/* <div style={{ float: "left", width: "70%", marginTop: "15px" }}>

        Uploaded File: <a href={url.fileUrl} style={{ fontWeight: "bold" }} target="_blank" rel="noopener noreferrer">
          {url.fileName}
        </a>


      </div> */}
      <div style={{ float: 'right', width: '30%', textAlign: 'right' }}>
        {getFileType(url) === 'image' ? (
          // Render image if it's an image file
          <a href={url} target="_blank" rel="noreferrer">
            <img src={url} style={{ maxWidth: '200px', maxHeight: '60px' }} alt="" />
          </a>
        ) : getFileType(url) === 'video' ? (
          // Render play button for video file
          <a href={url} target="_blank" rel="noreferrer">
            <img
              src="https://paulavatar.s3.us-east-2.amazonaws.com/play-button.png"
              style={{ maxWidth: '200px', maxHeight: '60px' }}
              alt="Play Video"
            />
          </a>
        ) : (
          // Fallback for other cases
          ''
        )}
      </div>
    </div>
  ) : null;
